// RB2 Core Connect
import * as Sentry from "@sentry/remix";
import {
  browserTracingIntegration,
  replayIntegration,
  thirdPartyErrorFilterIntegration,
} from "@sentry/remix";
import { useEffect } from "react";
import { useLocation, useMatches } from "@remix-run/react";

import { crawlerTag } from "~/lib/crawler";

function getPublicConfig() {
  // publicRuntimeConfig from @commercekit/remix is null on the client by design.
  // Remix serialises loader data into window.__remixContext before the bundle runs,
  // so we read the public config from there instead.
  return (window as any).__remixContext?.state?.loaderData?.root
    ?.publicRuntimeConfig as
    | { SENTRY_DSN?: string; APP_ENV?: string; GIT_COMMIT_SHA?: string }
    | undefined;
}

const HTML_ERROR_PAGE = /^\s*<!doctype html>/i;

const MAX_EVENTS_PER_MESSAGE = 5;
const seenCounts = new Map<string, number>();

function exceedsCap(key: string) {
  if (seenCounts.size > 200) seenCounts.clear();
  const next = (seenCounts.get(key) ?? 0) + 1;
  seenCounts.set(key, next);
  return next > MAX_EVENTS_PER_MESSAGE;
}

export function initSentryClient() {
  const config = getPublicConfig();
  if (!config?.SENTRY_DSN) return;
  // Local machines run with APP_ENV=PRODUCTION against the live API; keep them out of Sentry.
  if (process.env.NODE_ENV === "development" || config.GIT_COMMIT_SHA === "local-dev") return;

  Sentry.init({
    dsn: config.SENTRY_DSN,
    environment: config.APP_ENV ?? "DEVELOPMENT",
    release: config.GIT_COMMIT_SHA,
    tracesSampleRate: config.APP_ENV === "PRODUCTION" ? 0.2 : 1.0,
    attachStacktrace: true,
    ignoreErrors: [
      // Browser layout quirks
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Fetch cancelled by navigation or a lost connection
      /TypeError: Load failed/,
      /Failed to fetch/,
      "NetworkError when attempting to fetch resource",
      "Script error.",
      // GTM/Taggrs: unguarded dataLayer, and native bridges in in-app browsers
      "ReferenceError: ecommerce is not defined",
      "ReferenceError: Can't find variable: ecommerce",
      /window\.webkit\.messageHandlers/,
      /Java object is gone/,
      /Java exception was raised during method invocation/,
      // Scanners and blocked storage
      /Object Not Found Matching Id/,
      /Failed to read the 'sessionStorage' property/,
      "SecurityError: The operation is insecure.",
      /reCAPTCHA Timeout/,
      "NS_ERROR_FAILURE",
      // @remix-run/router 1.16.1 abort race; drop once the router is upgraded
      /Expected fetch controller/,
    ],
    beforeSend(event, hint) {
      const err = hint.originalException;
      if (err instanceof Error && err.name === "AbortError") return null;

      const exception = event.exception?.values?.[0];
      const value = exception?.value ?? event.message ?? "";
      // Read the original: prepareEvent truncates exception.value to 250 chars
      // before beforeSend runs, cutting off Cloudflare's status code.
      const rawValue =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : value;

      if (HTML_ERROR_PAGE.test(rawValue)) {
        const status = rawValue.match(/Error code (\d{3})/)?.[1] ?? "unknown";
        const summary = `Backend unreachable — origin returned an HTTP ${status} error page`;
        if (exception) {
          exception.value = summary;
        } else {
          event.message = summary;
        }
        event.fingerprint = ["backend-unreachable", status];
        event.tags = { ...event.tags, kind: "availability" };
        return exceedsCap(summary) ? null : event;
      }

      if (rawValue.includes("Invalid data: Key Name:")) {
        event.fingerprint = ["third-party-tag-invalid-data"];
        event.tags = { ...event.tags, owner: "marketing" };
        return exceedsCap(value) ? null : event;
      }

      const hasStack = Boolean(exception?.stacktrace?.frames?.length);
      if (!hasStack && (!value || value === "<unknown>" || value === "No error message")) {
        return null;
      }

      if (exceedsCap(value.slice(0, 120))) return null;
      return event;
    },
    denyUrls: [
      /www\.googletagmanager\.com/,
      /static\.klaviyo\.com/,
      /cdn-widgetsrepository\.yotpo\.com/,
      /cdn-4\.convertexperiments\.com/,
      /static\.widget\.trengo\.eu/,
      /static\.hotjar\.com/,
      /cdn\.cookie-script\.com/,
      /clarity\.ms/,
      /clarity\.js/,
      /ct\.pinterest\.com/,
    ],
    // Both 0 on purpose: checkout drives Replay manually (WOV-2039), so only
    // failing checkouts record.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      replayIntegration({
        networkDetailAllowUrls: [window.location.origin],
        networkRequestHeaders: ["X-Request-Id", "Content-Type"],
        networkResponseHeaders: ["X-Request-Id"],
      }),
      // Tag, not drop: the tag also lands on some first-party issues (FW, sitekey),
      // so dropping would hide real bugs.
      thirdPartyErrorFilterIntegration({
        filterKeys: ["wovar-webshop"],
        behaviour: "apply-tag-if-exclusively-contains-third-party-frames",
      }),
    ],
  });
  Sentry.setTag("shop", window.location.hostname.replace(/^www\./, ""));
  // Google renders pages in Chrome with its crawler name in the user agent; this tells its crashes apart.
  Sentry.setTag("crawler", crawlerTag(navigator.userAgent));
}

export { Sentry };
